import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus } from 'lucide-react';
import { Input } from '~/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

/** Tax-category choice for a new order customer (UI-level; parent maps to the server model). */
export type NewCustomerTaxCategory = 'retail' | 'invoice';

export interface NewCustomerPayload {
	firstName: string;
	lastName: string;
	email: string;
	taxCategory: NewCustomerTaxCategory;
	mobile: string;
	phone?: string;
}

interface CustomerCreateFormProps {
	/** Pre-fills the name from whatever the operator typed in the search box. */
	initialName?: string;
	/** Emits the assembled payload; the PARENT decides whether/how to create the customer. */
	onUse: (payload: NewCustomerPayload) => void;
	onCancel: () => void;
}

function emptyPayload(initialName?: string): NewCustomerPayload {
	const [firstName = '', lastName = ''] = (initialName ?? '').trim().split(/\s+/);
	return { firstName, lastName, email: '', taxCategory: 'retail', mobile: '', phone: '' };
}

/**
 * Inline create-customer form (HTML "inline-create"). Presentational: it never calls the create
 * endpoint — "Use this customer" emits the payload up to the parent, which owns the mutation.
 */
export function CustomerCreateForm({ initialName, onUse, onCancel }: CustomerCreateFormProps) {
	const { t } = useTranslation();
	const [payload, setPayload] = useState<NewCustomerPayload>(() => emptyPayload(initialName));

	const set = <K extends keyof NewCustomerPayload>(key: K, value: NewCustomerPayload[K]) => setPayload(prev => ({ ...prev, [key]: value }));

	const canUse =
		payload.firstName.trim() !== '' && payload.lastName.trim() !== '' && payload.email.trim() !== '' && payload.mobile.trim() !== '';

	return (
		<div className="rounded-md border border-dashed border-border bg-muted/40 p-4">
			<div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
				<UserPlus size={14} />
				{t('orders.components.customerPicker.createTitle')}
			</div>
			<p className="mt-1 text-[11px] text-foreground-muted">{t('orders.components.customerPicker.createHint')}</p>

			<div className="mt-3 grid grid-cols-2 gap-3">
				<Field label={t('orders.components.customerPicker.firstName')} required>
					<Input value={payload.firstName} onChange={e => set('firstName', e.target.value)} />
				</Field>
				<Field label={t('orders.components.customerPicker.lastName')} required>
					<Input value={payload.lastName} onChange={e => set('lastName', e.target.value)} />
				</Field>
				<Field label={t('orders.components.customerPicker.email')} required>
					<Input type="email" value={payload.email} onChange={e => set('email', e.target.value)} placeholder="customer@example.com" />
				</Field>
				<Field label={t('orders.components.customerPicker.taxCategory')}>
					<Select value={payload.taxCategory} onValueChange={v => set('taxCategory', v as NewCustomerTaxCategory)}>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="retail">{t('orders.components.customerPicker.taxRetail')}</SelectItem>
							<SelectItem value="invoice">{t('orders.components.customerPicker.taxInvoice')}</SelectItem>
						</SelectContent>
					</Select>
				</Field>
				<Field label={t('orders.components.customerPicker.mobile')} required>
					<Input value={payload.mobile} onChange={e => set('mobile', e.target.value)} placeholder="+30 6XX XXXXXXX" />
				</Field>
				<Field label={t('orders.components.customerPicker.phone')}>
					<Input value={payload.phone ?? ''} onChange={e => set('phone', e.target.value)} placeholder="+30 2XX XXXXXXX" />
				</Field>
			</div>

			<div className="mt-3 flex items-center justify-end gap-2">
				<Button type="button" variant="ghost" size="sm" onClick={onCancel}>
					{t('common.cancel')}
				</Button>
				<Button type="button" size="sm" disabled={!canUse} onClick={() => onUse(payload)}>
					{t('orders.components.customerPicker.useCustomer')}
				</Button>
			</div>
		</div>
	);
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
	return (
		<label className="flex flex-col gap-1">
			<span
				className={cn(
					'text-[11px] font-medium uppercase tracking-[0.04em] text-foreground-subtle',
					required && "after:ml-0.5 after:text-danger-fg after:content-['*']"
				)}>
				{label}
			</span>
			{children}
		</label>
	);
}
